import React, { useState, useEffect } from 'react';
import {
    find,
    forEach,
    get,
    isEmpty,
    isNil,
    map,
    orderBy,
    reduce,
    reverse,
    slice,
} from 'lodash';
import translateService from '../../common/translateService';
import {
    generateBlogRootUrl,
    creativeWorkStatusTypes,
} from '../../services/cms/cmsService';
import NoContent from '../NoContent.jsx';
import BlogHeader from './BlogHeader.jsx';
import TopBlogPosts from '../modules/blogposts/TopBlogPosts.jsx';
import Button from '../Button.jsx';
import BlogYouTube from './BlogYouTube.jsx';
import './BlogHomePage.scss';

const BlogHomePage = ({
    virtualdir,
    usercultureiso,
    usercountry,
    usercurrency,
    bloglistcontent,
    topics,
    banneranddescription,
    youtubeapikey,
}) => {
    const [listContent, setBlogListContent] = useState({});
    const [topicsList, setTopicsList] = useState(JSON.parse(topics));
    const [header, setHeader] = useState({
        banner: {},
        description: {},
        youtube: {},
    });
    const [latest, setLatest] = useState([]);

    useEffect(() => {
        const contentMappedByType = JSON.parse(bloglistcontent);
        const list = reduce(
            contentMappedByType,
            (acc, cmp) => {
                const items = JSON.parse(cmp.content).data;
                const templateType = cmp.templateType;
                const slugMapping = cmp.slugMapping;

                forEach(items, (item) => {
                    const attributes = item.attributes;
                    const topic = get(
                        attributes,
                        'mainCreativeWork.topic.keyword'
                    );
                    const slug = slugMapping[item.id];
                    let contentWithTemplateTypeAndSlug = {
                        templateType,
                        slug,
                        ...item,
                    };

                    if (
                        attributes.mainCreativeWork.creativeWorkStatus ===
                            creativeWorkStatusTypes.published &&
                        !isNil(find(topicsList, (t) => t.name === topic))
                    ) {
                        if (acc[topic]) {
                            acc[topic].push(contentWithTemplateTypeAndSlug);
                        } else {
                            acc[topic] = [contentWithTemplateTypeAndSlug];
                        }
                    }
                });
                return acc;
            },
            {}
        );

        forEach(list, (items, key) => {
            list[key] = reverse(
                orderBy(
                    items,
                    (i) => i.attributes.mainCreativeWork.datePublished
                )
            );
        });

        setBlogListContent(list);

        const allBlogPosts = reduce(
            contentMappedByType,
            (acc, cmp) => {
                const items = JSON.parse(cmp.content).data;
                const templateType = cmp.templateType;
                const slugMapping = cmp.slugMapping;

                forEach(items, (i) => {
                    const slug = slugMapping[i.id];
                    const contentWithTemplateTypeAndSlug = {
                        templateType,
                        slug,
                        ...i,
                    };
                    acc.push(contentWithTemplateTypeAndSlug);
                });
                return acc;
            },
            []
        );

        setLatest(
            slice(
                reverse(
                    orderBy(
                        allBlogPosts,
                        (p) => p.attributes.mainCreativeWork.datePublished
                    )
                ),
                0,
                8
            )
        );

        const bannerAndDescription = JSON.parse(banneranddescription);
        if (
            !isNil(bannerAndDescription) &&
            !isNil(bannerAndDescription.attributes)
        ) {
            const x = bannerAndDescription.attributes;
            const banner =
                !isNil(x.banner) &&
                !isNil(x.banner.data) &&
                !isNil(x.banner.data.attributes)
                    ? x.banner.data.attributes
                    : {};
            const description = !isNil(x.description) ? x.description : {};
            setHeader({
                banner: banner,
                description: description,
                youtube: x.youtube,
            });
        }
    }, []);

    const scrollToSection = (topicName) => {
        const element = document.getElementById(topicName);
        if (!isNil(element)) {
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
    };

    return listContent.length === 0 ? (
        <NoContent />
    ) : (
        <>
            <BlogHeader
                banner={header.banner}
                description={header.description}
                title={translateService.get('blog.title')}
            />
            <div className='blog-topics-container'>
                <div className='blog-topics'>
                    {map(topicsList, (topic, idx) => (
                        <div
                            className='blog-topic'
                            key={idx}
                            onClick={() => {
                                scrollToSection(topic.name);
                            }}>
                            {translateService.get('blog.' + topic.name)}
                        </div>
                    ))}
                </div>
            </div>
            <div className='blog-posts-container'>
                <div className='blog-posts-section latest'>
                    <div className='blog-posts-header'>
                        <h2>{translateService.get('blog.latest')}</h2>
                    </div>
                    <TopBlogPosts blogposts={latest} showInCarousel={true} />
                </div>
            </div>
            {!isNil(header.youtube) && !isEmpty(header.youtube) ? (
                <div className='blog-posts-container youtube'>
                    <div className='blog-posts-section'>
                        <BlogYouTube
                            mainVideoId={header.youtube.mainVideo}
                            secondaryVideoIds={header.youtube.videos}
                            youtubeapikey={youtubeapikey}
                        />
                    </div>
                </div>
            ) : (
                <></>
            )}
            <div className='blog-posts-container'>
                {map(listContent, (data, t) => {
                    const topic = find(topicsList, (x) => x.name === t);
                    return isNil(topic) ? (
                        <></>
                    ) : (
                        <div
                            className='blog-posts-section'
                            key={topic.name}
                            id={topic.name}>
                            <div className='blog-posts-header'>
                                <div></div>
                                <h2>
                                    {translateService.get(`blog.${topic.name}`)}
                                </h2>
                                <div></div>
                            </div>
                            {data && data.length > 0 && (
                                <TopBlogPosts
                                    blogposts={data}
                                    showInCarousel={true}
                                />
                            )}
                            <div className='blog-posts-cta'>
                                <a
                                    href={`${generateBlogRootUrl()}/category/${
                                        topic.slug
                                    }`}>
                                    <Button
                                        className='button button--primary'
                                        text={translateService.get(
                                            'blog.' + topic.name
                                        )}
                                    />
                                </a>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default BlogHomePage;
